import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import CheckIcon from '@mui/icons-material/Check';
import { IconButton } from "@material-ui/core";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@material-ui/icons";
import clsx from "clsx";

const styles = makeStyles((theme) => ({
	selectedCategoryRectangle: ({ serviceLayout, isOld }) => {
		return {
			height: "28px",
			background: serviceLayout ? (isOld ? "#222" :"#fff") : "transparent",
			display: "flex",
			margin: "0 4px 5px 0",
			borderRadius: "14px",
			alignItems: "center",
			justifyContent: "space-around",
			flexDirection: "row",
			padding: "10px",
		}
	},
	selectedCategory: {
		fontSize: "14px",
		fontWeight: 300,
		fontStretch: "normal",
		fontStyle: "normal",
		lineHeight: 1.33,
		letterSpacing: -0.4,
		borderBottom: " none",
		display: "flex",
		margin: 0,
	},
	selectedColor: ({ serviceLayout, isOld }) => {
		return {
			backgroundColor: serviceLayout ? (isOld ? "#222" : "#fff") : "#450067",
			color: serviceLayout ? isOld ? "#fff" : "#222" : "#fff",
		}
	},
	unselectedColor: ({ serviceLayout, isOld }) => {
		return {
			color: serviceLayout ? isOld ? "#fff" : "#222" : "#672E37",
			fontWeight: 400,
		}
	},
	selectedColorSvg: {
		fill: "#fff",
	},
	unselectedColorSvg: {
		fill: "#450067",
	},
	horizontalScroll: ({ showCheck }) => {
		return {
			display: "flex", 
			flexWrap: "nowrap", 
			alignItems: "center", 
			height: "100%", 
			padding: !showCheck ? '0px 10px' : '0px'
		}
	},
	default: {
		display: "flex", 
		flexWrap: "wrap"
	}, 

	singleServiceContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		width: "100%",
		padding: "0 10px",
	},
	singleServiceContent: {
		display: "flex",
		alignItems: "center",
		justifyContent: "start",
		paddingLeft: 20,
		marginBottom: 15
	},
	navigationButton: {
		color: "#fff",
		padding: "8px",
		minWidth: "40px",
		height: "40px",
		borderRadius: "50%",
		backgroundColor: "rgba(0,0,0,0.1)",
		"&:hover": {
			backgroundColor: "rgba(0,0,0,0.2)",
		},
		"&:disabled": {
			backgroundColor: "rgba(0,0,0,0.05)",
			color: "rgba(0,0,0,0.3)",
		}
	},
	serviceCounter: {
		fontSize: "12px",
		color: "rgba(0,0,0,0.6)",
		marginTop: "5px",
		textAlign: "center",
	},

	// Animação de entrada inicial da esquerda para o centro
	"@keyframes slideInFromLeftInitial": {
		"0%": {
			transform: "translateX(-100%) scale(0.8)",
			opacity: 0,
		},
		"100%": {
			transform: "translateX(0) scale(1)",
			opacity: 1,
		}
	},

	// Animação de saída para a direita (quando avança)
	"@keyframes slideOutToRight": {
		"0%": {
			transform: "translateX(0) scale(1)",
			opacity: 1,
		},
		"100%": {
			transform: "translateX(100%) scale(0.8)",
			opacity: 0,
		}
	},

	// Animação de saída para a esquerda (quando retrocede)
	"@keyframes slideOutToLeft": {
		"0%": {
			transform: "translateX(0) scale(1)",
			opacity: 1,
		},
		"100%": {
			transform: "translateX(-100%) scale(0.8)",
			opacity: 0,
		}
	},

	// Animação de entrada da esquerda para o centro (quando avança)
	"@keyframes slideInFromLeft": {
		"0%": {
			transform: "translateX(-100%) scale(0.8)",
			opacity: 0,
		},
		"100%": {
			transform: "translateX(0) scale(1)",
			opacity: 1,
		}
	},

	// Animação de entrada da direita para o centro (quando retorna)
	"@keyframes slideInFromRight": {
		"0%": {
			transform: "translateX(100%) scale(0.8)",
			opacity: 0,
		},
		"100%": {
			transform: "translateX(0) scale(1)",
			opacity: 1,
		}
	},

	// Classes para animações
	slideInInitial: {
		animation: `$slideInFromLeftInitial 0.5s ease-out forwards`,
	},
	slideOutAnimationNext: {
		animation: `$slideOutToRight 0.3s ease-in forwards`,
	},
	slideOutAnimationPrev: {
		animation: `$slideOutToLeft 0.3s ease-in forwards`,
	},
	slideInAnimationNext: {
		animation: `$slideInFromLeft 0.4s ease-out forwards`,
	},
	slideInAnimationPrev: {
		animation: `$slideInFromRight 0.4s ease-out forwards`,
	}
}));

const FeaturesSelected = (props) => {
	const {
		features,
		currentIndex,
		setCurrentIndex,
		levelPath,
		showPath = false,
		serviceLayout = false,
		showCheck = false,
		singleServiceView = false,
		animate,
		externalAnimationDirection
	} = props;
	const isOld = props?.isOld;
	const classes = styles({ serviceLayout, isOld, showCheck });

	const [isAnimating, setIsAnimating] = useState(false);
	const [animationDirection, setAnimationDirection] = useState('');
	const [isInitialRender, setIsInitialRender] = useState(true);

	useEffect(() => {
		if (isInitialRender) {
			const timer = setTimeout(() => {
				setIsInitialRender(false);
			}, 500); 
			return () => clearTimeout(timer);
		}
	}, [isInitialRender]);

	useEffect(() => {
		if (animate && !isInitialRender && !isAnimating) {
			setIsAnimating(true);
			setAnimationDirection(externalAnimationDirection);
			
			setTimeout(() => {
				setIsAnimating(false);
			}, 300);
		}
	}, [animate]);

	const handlePrevious = () => {
		if (currentIndex > 0 && !isAnimating) {
			setIsAnimating(true);
			setAnimationDirection('prev');
			
			setTimeout(() => {
				setCurrentIndex(currentIndex - 1);
				setIsAnimating(false);
			}, 300);
		}
	};

	if (singleServiceView) {
		if (!features || features.length === 0) {
			return null;
		}

		const currentService = features[currentIndex];

		return (
			<div className={classes.singleServiceContainer}>
				<div className={classes.singleServiceContent}>
					<div
						key={`service-${currentIndex}-${currentService.id}-${animate}`}
						className={clsx({
							[classes.selectedCategoryRectangle]: true,
							[classes.selectedColor]: true,
							[classes.slideInInitial]: isInitialRender,
							[classes.slideOutAnimationNext]: !isInitialRender && isAnimating && animationDirection === 'next',
							[classes.slideOutAnimationPrev]: !isInitialRender && isAnimating && animationDirection === 'prev',
							[classes.slideInAnimationNext]: !isInitialRender && !isAnimating && animationDirection === 'next',
							[classes.slideInAnimationPrev]: !isInitialRender && !isAnimating && animationDirection === 'prev',
						})}
						style={{
							border: "1px solid rgba(0,0,0,0.3)",
							minWidth: "200px",
							maxWidth: "calc(100vw - 60px)",
							overflow: "hidden",
							justifyContent: "center",
						}}
					>
						<div style={{ margin: "0 6px", overflow: "hidden" }}>
							<h1
								translate="no"
								className={clsx({
									[classes.selectedCategory]: true,
									[classes.selectedColor]: true,
								})}
								style={{
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									maxWidth: "calc(100vw - 100px)",
									display: "flex",
									alignItems: "center",
									justifyContent: "flex-start",
								}}
							>
								{showCheck && (
									<CheckIcon style={{ fontSize: "14px", marginRight: "2px", color: "green" }} />
								)}
								{currentService.serviceName !== undefined ? currentService.serviceName : currentService?.name ?? ""}
							</h1>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className={clsx({
			[classes.horizontalScroll]: serviceLayout,
			[classes.default]: !serviceLayout
		})}>
			{ (showPath && features?.length === 0 && levelPath?.length > 0) && (
				<div
					className={classes.selectedCategoryRectangle}
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexWrap: "wrap",
						maxWidth: "100%",
						padding: "2px 14px",
						gap: "2px",
						border: "1px solid #555",
						marginBottom: "12px",
					}}
				>
					{levelPath.map((item, idx) => (
						<span
							key={item.id}
							style={{ display: "flex", alignItems: "center" }}
						>
							<div
								className={classes.unselectedColor}
								style={{ cursor: "pointer", display: "inline", whiteSpace: "nowrap" }}
							>
								{item?.name}
							</div>
							{idx < levelPath.length - 1 && <span style={{ margin: "0 4px", color: "rgba(0,0,0,0.4)" }}>-</span>}
						</span>
					))}
				</div>
			)}
			{features.map((item, index) => {
				return (
					<div
						key={`nameSelected-${index}`}
						className={clsx({
							[classes.selectedCategoryRectangle]: true,
							[classes.selectedColor]: currentIndex == index,
							[classes.unselectedColor]: currentIndex != index,
						})}
						style={{
							border: "1px solid rgba(0,0,0,0.3)",
						}}
						onClick={() => setCurrentIndex(index)}
						id="featuresDiv"
						name="featuresDiv">
						<div style={{ margin: "0 6px" }} id="featureName" name="featureName">
							<h1
								translate="no"
								className={clsx({
									[classes.selectedCategory]: true,
									[classes.selectedColor]: currentIndex == index,
									[classes.unselectedColor]: currentIndex != index,
								})}
								style={{
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									width: "100%", 
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
								id="featureName"
								name="featureName">
								{showCheck && (
									<CheckIcon style={{ fontSize: "14px", marginRight: "2px", color: "green" }} />
								)}
								{item.serviceName !== undefined ? item.serviceName : item?.name ?? ""}
							</h1>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default FeaturesSelected;
